'use es6';

import { EmbedSizes, ResponsiveElligibleEmbeds, ContentEmbedUIEvents } from 'rich-text-lib/constants/embed';
import { Alignments } from 'rich-text-lib/constants/alignments';
import { trackInteraction } from '../utils/usageTracking';
import { OPEN_EMBED_MODAL } from '../constants/usageTracking';
import { getAlignment, resizeAssetDimensions } from '../utils/shared';
import { getOnChangeAlignment, getSizeChangeHandler } from '../utils/embed';
import { RESPONSIVE_EMBED_FULL_WIDTH_CLASS, DEFAULT_EMBED_WIDTH, HS_EMBED_WRAPPER_CLASS, HS_EMBED_INNER_WRAPPER_CLASS } from '../constants/hsMedia';
import { calcAspectRatio, getResponsiveEmbedFullWidthStyles } from './util';
export const getEmbedClickHandler = (editor, openEmbedModal) => () => {
  openEmbedModal();
  trackInteraction(editor, 'tinymceEmbed', OPEN_EMBED_MODAL);
};
export const getObjectResizedHandler = (editor, updateHtml, maxContainerWidth) => event => {
  const dom = editor.dom;
  let {
    width,
    height
  } = event;
  const {
    target
  } = event;
  if (editor.dom.hasClass(target, HS_EMBED_WRAPPER_CLASS) && width && height) {
    const resizedDimensions = resizeAssetDimensions(maxContainerWidth, width, height);

    // This triggers the iframe embed to resize
    // which calls the resize handlers
    dom.setStyles(target, {
      'max-width': `${resizedDimensions.width}px`,
      'max-height': `${resizedDimensions.height}px`
    });
    return;
  }
  const embedType = target.getAttribute('data-mce-object');
  if (embedType == null || dom.hasClass(target, 'hs-video')) {
    if (dom.hasClass(target, 'hs-embeddable') && target.getAttribute('data-embed-type') === 'wistia') {
      dom.setAttrib(target, 'width', width);
      dom.setAttrib(target, 'height', height);
      dom.setStyles(target, {
        width: `${width}px`,
        height: `${height}px`
      });
    }
    return;
  }
  if (width && height) {
    let targetEl = target;
    if (embedType === 'iframe') {
      targetEl = dom.select('iframe', target);
    } else if (embedType === 'video') {
      targetEl = dom.select('video', target);
    }
    const resizedDimensions = resizeAssetDimensions(maxContainerWidth, width, height);
    width = resizedDimensions.width;
    height = resizedDimensions.height;
    dom.setAttrib(targetEl, 'width', width);
    dom.setAttrib(targetEl, 'height', height);
    if (ResponsiveElligibleEmbeds.includes(target.getAttribute('data-mce-p-data-service'))) {
      dom.setStyle(target, {
        'max-width': `${width}px`,
        'max-height': `${height}px`
      });
    } else {
      dom.setStyles(target, {
        width: `${width}px`,
        height: `${height}px`
      });
    }
  }
  let html;
  html = target.getAttribute('data-mce-html');
  if (html) {
    html = decodeURI(html);
    target.setAttribute('data-mce-html', encodeURI(updateHtml(html, {
      width,
      height
    })));
  }
};
export const getEmbedSizeChangeHandler = (editor, defaultEmbedStyles) => event => {
  const dom = editor.dom;
  const {
    target,
    size
  } = event;
  const embedType = target.getAttribute('data-mce-object');
  if (dom.hasClass(target, HS_EMBED_WRAPPER_CLASS)) {
    editor.undoManager.transact(() => {
      if (size === EmbedSizes.FULL) {
        // Add fullwidth class and set responsive wrapper styles
        dom.addClass(target, RESPONSIVE_EMBED_FULL_WIDTH_CLASS);
        dom.setStyles(target, getResponsiveEmbedFullWidthStyles());
      } else if (size === EmbedSizes.CUSTOM) {
        // Remove fullwidth class and reset fixed size wrapper styles
        const iframeEl = dom.select('iframe', target);
        const width = iframeEl && dom.getAttrib(iframeEl, 'width');
        const height = iframeEl && dom.getAttrib(iframeEl, 'height');
        dom.removeClass(target, RESPONSIVE_EMBED_FULL_WIDTH_CLASS);
        dom.setStyles(target, {
          'max-width': `${width || DEFAULT_EMBED_WIDTH}px`,
          'max-height': height ? `${height}px` : null
        });
      }
    });
    return;
  }
  if (embedType == null || dom.hasClass(target, 'hs-video') || embedType !== 'iframe') {
    return;
  }
  const targetEl = dom.select('iframe', target);
  let styleMap;
  editor.undoManager.transact(() => {
    if (size === EmbedSizes.FULL) {
      // Add fullwidth class to iframe and set styles for the wrapper
      dom.addClass(targetEl, RESPONSIVE_EMBED_FULL_WIDTH_CLASS);
      styleMap = getResponsiveEmbedFullWidthStyles();
    } else if (size === EmbedSizes.CUSTOM) {
      // Remove fullwidth class and reset wrapper styles back to default
      dom.removeClass(targetEl, RESPONSIVE_EMBED_FULL_WIDTH_CLASS);
      const width = dom.getAttrib(targetEl, 'width');
      const height = dom.getAttrib(targetEl, 'height');
      styleMap = {
        display: null,
        'margin-left': '0px',
        'margin-right': '0px',
        'max-width': `${width}px`,
        'max-height': `${height}px`
      };
      if (defaultEmbedStyles) {
        Object.keys(defaultEmbedStyles).forEach(key => {
          styleMap[key] = defaultEmbedStyles[key];
        });
      }
    }
    dom.setStyles(target, styleMap);
  });
};
export const getLayoutChangeHandler = editor => {
  const updateAlignment = getOnChangeAlignment(editor);
  const updateSize = getSizeChangeHandler(editor);
  return (target, alignment) => {
    const targetEl = editor.dom.hasClass(target, HS_EMBED_WRAPPER_CLASS) ? target : editor.dom.select('iframe', target);
    const curFullWidth = targetEl && editor.dom.hasClass(targetEl, RESPONSIVE_EMBED_FULL_WIDTH_CLASS);
    const curAlignment = curFullWidth ? null : getAlignment(target);
    if (alignment === Alignments.JUSTIFY) {
      updateSize(target, curFullWidth ? EmbedSizes.CUSTOM : EmbedSizes.FULL);
    } else {
      if (curFullWidth) {
        updateSize(target, EmbedSizes.CUSTOM);
      }
      updateAlignment(target, alignment, curAlignment);
    }
  };
};

// Postmessage the embed content to the target iframe embed app.
// The content is held in the [data-mce-iframe-embed-content] attr
export const getSetIframeContentHandler = (editor, targetOrigin) => id => {
  const wrappers = editor.dom.select(`[data-mce-iframe-embed-id="${id}"]`);
  if (wrappers.length !== 1) {
    // Prevent unnecessary set content calls if no wrapper exists for
    // the requesting id (ie. for modal preview iframe) or
    // from duplicate iframes (ie. for drag-container clones).
    // We've replaced mce-offscreen-selection duplicate iframes by
    // placeholder images and changing [data-mce-iframe-embed-id]
    return;
  }
  const targetWrapper = wrappers[0];
  const targetIframe = targetWrapper.querySelector('iframe');
  targetIframe.contentWindow.postMessage({
    action: ContentEmbedUIEvents.SET_CONTENT,
    id,
    payload: {
      html: targetWrapper.dataset.mceEmbedContent
    }
  }, targetOrigin);
};

// Adjust the wrapper size given the embed iframe size change
export const getAdjustIframeSizeHandler = editor => (id, data) => {
  let {
    width,
    height
  } = data;
  width = Math.round(width);
  height = Math.round(height);
  const dimensions = {
    width,
    height
  };

  // Don't resize the iframe wrappers if there is no height
  if (!height) return;
  const wrapper = editor.dom.select(`[data-mce-iframe-embed-id="${id}"]`)[0];
  if (!wrapper) return;
  const innerWrapper = wrapper.querySelector(`.${HS_EMBED_INNER_WRAPPER_CLASS}`);
  const iframeEl = wrapper.querySelector('iframe');
  const isFullWidth = wrapper.classList.contains(RESPONSIVE_EMBED_FULL_WIDTH_CLASS);
  if (!isFullWidth) {
    editor.dom.setStyles(wrapper, {
      'max-width': `${width}px`,
      'max-height': `${height}px`
    });
    editor.dom.setAttribs(iframeEl, dimensions);
  }
  editor.dom.setStyle(innerWrapper, 'padding-bottom', calcAspectRatio(dimensions));
};

// Replace the targetClone iframe with a dummy placeholder so we don't
// unnecessarily render a duplicate [content-embed-ui] iframe in the
// mce-offscreen-selection container (this also prevents triggering setContent)
export const getIframeEmbedSelectedHandler = editor => evt => {
  const {
    target,
    targetClone
  } = evt;
  const isIframeEmbed = editor.dom.hasClass(target, HS_EMBED_WRAPPER_CLASS);
  const hasScripts = !!target.dataset.scriptEmbed;
  if (isIframeEmbed && hasScripts) {
    // Remove the [data-mce-iframe-embed-id] to ignore this clone
    delete targetClone.dataset.mceIframeEmbedId;
    const iframeReplacement = document.createElement('div');
    const targetIframe = targetClone.querySelector('iframe');
    targetIframe.parentElement.replaceChild(iframeReplacement, targetIframe);
  }
};
let dragTarget;
export const getIframeEmbedDragStartHandler = () => ({
  target
}) => {
  if (target && target.classList && target.classList.contains(HS_EMBED_WRAPPER_CLASS)) {
    dragTarget = target;
  }
};
export const getIframeEmbedDropHandler = editor => evt => {
  if (!dragTarget) return;
  const hasScripts = !!dragTarget.dataset.scriptEmbed;
  if (hasScripts) {
    // Override tinymce drop handler in order to short ciruit the pre process
    // transforms on the placeholder element, and insert after the drop target
    const dropNode = editor.selection.getNode();
    dropNode.parentNode.insertBefore(dragTarget, dropNode.nextSibling);
    evt.preventDefault();
    evt.stopPropagation();
  }
  dragTarget = null;
};